import React, { useEffect } from "react";
import RadioInput from "uicomponents/RadioInput";
import DocumentUpload from "uicomponents/DocumentUpload";
import TextInput from "uicomponents/TextInput";
import DateInput from "uicomponents/DateInput";
import SelectInput from "uicomponents/SelectInput";
import {
  peoOptions,
  validRenewalDate,
} from "pages/CensusAndCurrentInformation/utils/helper";
import TextArea from "uicomponents/TextArea";
import { useFormikContext } from "formik";
import { CurrentInsuranceFormType } from "pages/CensusAndCurrentInformation/utils/formTypes";
import { CurrentInsuranceType } from "types";
import styles from "../CurrentInsurance.module.css";
import { SystemIcon } from "@justworkshr/milo-components";
import { getAppType } from "utils/helper";

interface ICurrentInsuranceComponentProps {
  data?: CurrentInsuranceType;
}

const CurrentInsuranceComponent = ({
  ...props
}: ICurrentInsuranceComponentProps): JSX.Element => {
  const { values, setFieldValue } = useFormikContext();
  const isHealthInsuranceUpsellApp = getAppType() === "HEALTH_INSURANCE_UPSELL";

  const hasHealthInsurance =
    props.data?.healthInsuranceInformation?.currentlyOfferHealthInsurance;
  const currentRenewalDate =
    props.data?.healthInsuranceInformation?.currentRenewalDate;

  const isOnPeo = (values as CurrentInsuranceFormType)?.isOnPeo;
  const hasCurrentPeo = (values as CurrentInsuranceFormType)?.currentPeo;

  useEffect(() => {
    if (isOnPeo === "No") {
      const updateFieldList = ["currentPeo", "currentPeoOther"];
      updateFieldList.forEach((field) => {
        setFieldValue(field, "");
      });
    }
  }, [isOnPeo, setFieldValue]);

  useEffect(() => {
    if (hasCurrentPeo !== "Other") {
      setFieldValue("currentPeoOther", "");
    }
  }, [hasCurrentPeo, setFieldValue]);

  return (
    <div>
      <TextInput
        name="sicCode"
        label="What is your company’s Standard Industrial Classification (SIC) code?"
      />
      <a
        href="https://siccode.com/sic-code-lookup-directory"
        target="_blank"
        rel="noopener noreferrer"
        className={styles.link}
      >
        Find your company’s code
        <SystemIcon
          iconName="external-link"
          color="brand"
          title="person"
          size="small"
        />
      </a>
      <TextInput
        name="yearFounded"
        label="What year was your company founded?"
        placeholder="YYYY"
      />
      <RadioInput
        name="controlledGroup"
        label="Are you part of a controlled group?"
        options={["Yes", "No"]}
      />
      <a
        href="https://help.justworks.com/hc/en-us/articles/360018609291-Related-Entities"
        target="_blank"
        rel="noopener noreferrer"
        className={styles.link}
      >
        Learn about controlled groups
        <SystemIcon
          iconName="external-link"
          color="brand"
          title="person"
          size="small"
        />
      </a>
      <DateInput
        name="startingCoverageDate"
        label="When will you need your coverage to begin?"
      />
      {hasHealthInsurance && (
        <>
          <DocumentUpload
            name="healthInsuranceInvoice"
            label={"Attach a PDF of your current health insurance invoice"}
            application={props.data?.healthInsuranceApplication?.[0]}
          />
          <a
            href="/health-insurance-application/invoice_sample_document"
            target="_blank"
            rel="noopener noreferrer"
            className={styles.link}
          >
            Health Insurance Invoice
            <SystemIcon
              iconName="external-link"
              color="brand"
              title="person"
              size="small"
            />
          </a>
          <DocumentUpload
            name="sbcDocument"
            label={
              "Attach a PDF of all summary of benefits and coverage (SBC) for the plans you currently offer"
            }
            application={props.data?.healthInsuranceApplication?.[0]}
          />
          <a
            href="/health-insurance-application/sbc_sample_document"
            target="_blank"
            rel="noopener noreferrer"
            className={styles.link}
          >
            Summary of Benefits and Coverage (SBC)
            <SystemIcon
              iconName="external-link"
              color="brand"
              title="person"
              size="small"
            />
          </a>
          {!validRenewalDate(currentRenewalDate) && (
            <>
              <DocumentUpload
                name="renewalRateDocument"
                label={"Attach a PDF of your renewal rate"}
                application={props.data?.healthInsuranceApplication?.[0]}
              />
              <a
                href="/health-insurance-application/renewal_rate_sample_document"
                target="_blank"
                rel="noopener noreferrer"
                className={styles.link}
              >
                Health Insurance Renewal Rate
                <SystemIcon
                  iconName="external-link"
                  color="brand"
                  title="person"
                  size="small"
                />
              </a>
            </>
          )}
          {!isHealthInsuranceUpsellApp && (
            <>
              <RadioInput
                name="isOnPeo"
                label={"Are you currently on a PEO?"}
                options={["Yes", "No"]}
              />
              <a
                href="https://www.justworks.com/blog/whats-a-peo"
                target="_blank"
                rel="noopener noreferrer"
                className={styles.link}
              >
                Learn about PEOs
                <SystemIcon
                  iconName="external-link"
                  color="brand"
                  title="person"
                  size="small"
                />
              </a>
            </>
          )}
          {isOnPeo === "Yes" && (
            <>
              <SelectInput
                name="currentPeo"
                options={peoOptions}
                label="Which PEO?"
                placeholder="Select an option"
              />
              {hasCurrentPeo === "Other" && (
                <TextArea name="currentPeoOther" label="Please specify" />
              )}
            </>
          )}
        </>
      )}
    </div>
  );
};

export default CurrentInsuranceComponent;
